import { useI18n } from 'vue-i18n'

const SMALL_WORDS = new Set([
  'a',
  'an',
  'and',
  'as',
  'at',
  'but',
  'by',
  'for',
  'from',
  'in',
  'into',
  'nor',
  'of',
  'on',
  'onto',
  'or',
  'per',
  'the',
  'to',
  'up',
  'via',
  'with',
  'vs',
])

const ACRONYMS = new Set(['api', 'ai', 'ui', 'ux', 'sdk', 'sla', 'sso', 'id', 'ip'])

const BRAND_OVERRIDES: Record<string, string> = {
  iphone: 'iPhone',
  ebay: 'eBay',
}

const PLACEHOLDER_RE = /\{\{[^}]+}}|\{\d+}/
const WORD_RE = /[A-Za-z0-9]+(?:'[A-Za-z0-9]+)*/
const TOKEN_RE = new RegExp(`${PLACEHOLDER_RE.source}|${WORD_RE.source}|[.!?]`, 'g')

const capitalize = (word: string) =>
  word ? word[0].toUpperCase() + word.slice(1).toLowerCase() : word

const hasInternalUpper = (word: string) =>
  /[A-Z]/.test(word) && /[a-z]/.test(word) && !/^[A-Z][a-z]+$/.test(word)

const isAcronym = (word: string) => /^[A-Z0-9]+$/.test(word)

const toTitleCaseWord = (word: string, isFirst: boolean, isLast: boolean) => {
  const raw = word
  const lower = raw.toLowerCase()

  if (BRAND_OVERRIDES[lower]) return BRAND_OVERRIDES[lower]
  if (isAcronym(raw) || hasInternalUpper(raw)) return raw
  if (ACRONYMS.has(lower)) return lower.toUpperCase()

  if (!isFirst && !isLast && SMALL_WORDS.has(lower)) return lower

  return capitalize(lower)
}

interface Token {
  type: 'word' | 'placeholder' | 'punct' | 'other'
  value: string
}

const tokenize = (text: string): Token[] => {
  const tokens: Token[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = TOKEN_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({ type: 'other', value: text.slice(lastIndex, match.index) })
    }

    const value = match[0]
    if (PLACEHOLDER_RE.test(value)) {
      tokens.push({ type: 'placeholder', value })
    } else if (value === '.' || value === '!' || value === '?') {
      tokens.push({ type: 'punct', value })
    } else {
      tokens.push({ type: 'word', value })
    }

    lastIndex = TOKEN_RE.lastIndex
  }

  if (lastIndex < text.length) {
    tokens.push({ type: 'other', value: text.slice(lastIndex) })
  }

  return tokens
}

const isLastWordInSentence = (tokens: Token[], index: number) => {
  for (let i = index + 1; i < tokens.length; i += 1) {
    const token = tokens[i]
    if (token.type === 'punct') return true
    if (token.type === 'word') return false
  }
  return true
}

const titleCaseEnglish = (text: string) => {
  const tokens = tokenize(text)
  let wordIndexSincePunct = 0

  const result = tokens.map((token, index) => {
    if (token.type === 'punct') {
      wordIndexSincePunct = 0
      return token.value
    }

    if (token.type !== 'word') return token.value

    const isFirst = wordIndexSincePunct === 0
    const isLast = isLastWordInSentence(tokens, index)
    const transformed = toTitleCaseWord(token.value, isFirst, isLast)
    wordIndexSincePunct += 1
    return transformed
  })

  return result.join('')
}

/**
 * Provides i18n helpers with a locale-aware Title Case variant.
 *
 * `t` returns the raw translation as-is.
 * `tc` returns the translation transformed to Title Case **only for English locales**
 * (e.g., "add intent" -> "Add Intent"), and returns the original string for other locales.
 *
 * @example
 * const { t, tc } = useCasedI18n()
 * t('add_intent')  // "Add intent" (raw)
 * tc('add_intent') // "Add Intent" (Title Case for en)
 */
export const useCasedI18n = () => {
  const { t, locale } = useI18n({ useScope: 'global' })

  const tc = (key: string, params?: unknown) => {
    const value = t(key, params as Record<string, unknown>)
    return locale.value.startsWith('en') ? titleCaseEnglish(value) : value
  }

  return { t, tc, locale }
}
